// Ported from sn-plugin2 packages/embed-contract/src/snippet.ts @ 54685d64 — keep in step with the plugin.
import type { PluginPublicCfg } from '../types/embedSettings'

const ID_FIELDS = ['orgId', 'competitionId', 'teamId'] as const

const kebab = (name: string): string =>
  name.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)

/** `data-hide-tabs` entries, one `view.key` per hidden tab. */
const hiddenTabs = (tabs: PluginPublicCfg['tabs']): string[] =>
  Object.entries(tabs ?? {}).flatMap(([view, section]) =>
    Object.entries(section ?? {})
      .filter(([, shown]) => !shown)
      .map(([tab]) => `${view}.${tab}`)
  )

/** Footer, schedule view and layout; unset values are skipped. */
const displayAttributes = (
  display: PluginPublicCfg['display']
): Record<string, string> => {
  const attributes: Record<string, string> = {}
  const { footer, scheduleView, maxHeight, maxWidth, align, hideBorder } =
    display ?? {}
  if (typeof footer === 'string') {
    attributes['data-footer'] = footer
  } else if (footer && typeof footer === 'object') {
    const { text, link } = footer as { text?: string; link?: string }
    if (text) {
      attributes['data-footer-text'] = text
    }
    if (link) {
      attributes['data-footer-link'] = link
    }
  }
  if (typeof scheduleView === 'string') {
    attributes['data-schedule-view'] = scheduleView
  }
  if (typeof maxHeight === 'number') {
    attributes['data-max-height'] = String(maxHeight)
  }
  if (typeof maxWidth === 'number') {
    attributes['data-max-width'] = String(maxWidth)
  }
  if (typeof align === 'string') {
    attributes['data-align'] = align
  }
  if (hideBorder) {
    attributes['data-hide-border'] = 'true'
  }
  return attributes
}

/** The `data-*` attributes that carry a public cfg (the no-script snippet). */
export const snippetDataAttributes = (
  pluginKey: string,
  cfg: PluginPublicCfg
): Record<string, string> => {
  const attributes: Record<string, string> = {
    'data-key': pluginKey,
    'data-view': cfg.view,
  }
  for (const field of ID_FIELDS) {
    const id = cfg[field]
    if (id) {
      attributes[`data-${kebab(field)}`] = id
    }
  }
  if (cfg.initialTab) {
    attributes['data-initial-tab'] = cfg.initialTab
  }
  const { mode, scoreboard, ...rest } = cfg.theme ?? {}
  for (const [name, value] of Object.entries(rest)) {
    if (value === '') {
      continue
    }
    attributes[`data-${kebab(name)}`] = String(value)
  }
  if (mode && mode !== 'auto') {
    attributes['data-mode'] = String(mode)
  }
  // The sport photo is the default, so only the header style is written.
  if (scoreboard === 'header') {
    attributes['data-scoreboard'] = scoreboard
  }
  const hidden = hiddenTabs(cfg.tabs)
  if (hidden.length > 0) {
    attributes['data-hide-tabs'] = hidden.join(',')
  }
  if (cfg.display?.hideHeader) {
    attributes['data-hide-header'] = 'true'
  }
  Object.assign(attributes, displayAttributes(cfg.display))
  if (cfg.syncUrl === false) {
    attributes['data-sync-url'] = 'false'
  }
  return attributes
}

const ATTRIBUTE_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '"': '&quot;',
  '<': '&lt;',
  '>': '&gt;',
}

/** A value safe inside a double-quoted HTML attribute (one pass, so `&` is never escaped twice). */
export const escapeAttribute = (value: string): string =>
  value.replace(/[&"<>]/g, (char) => ATTRIBUTE_ESCAPES[char] ?? char)
